import { useEffect, useState, type JSX } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  PublicApiError,
  getPublicSession,
  mockCompleteSession,
  type PublicSession,
} from '../api/public-sessions';
import {
  requestTossCardPayment,
  TossSdkError,
} from '../lib/toss';

/**
 * CO-01 Checkout page from docs/en/05-uiux-design.md §2.4.
 *
 * Phase 0 scope: reads the session id and checkout JWT from the URL,
 * fetches the public session view, renders amount + product + client
 * name + a single "Pay (mock)" button. Clicking "Pay" calls the Phase 0
 * mock-complete endpoint and redirects to the success URL the client
 * product supplied at session creation time.
 *
 * This page is intentionally minimal — the full checkout UX polish
 * (per-product theming, timer countdown, localization, etc.) lands
 * when real providers and the theme system come online.
 */
export function CheckoutPage(): JSX.Element {
  const { sessionId = '' } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();

  const [session, setSession] = useState<PublicSession | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId || !token) {
      setLoadError('Missing session id or token');
      return;
    }
    let cancelled = false;
    getPublicSession(sessionId, token)
      .then((s) => {
        if (!cancelled) {
          setSession(s);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof PublicApiError) {
          if (err.payload.code === 'session_expired') {
            navigate('/expired', { replace: true });
            return;
          }
          setLoadError(err.payload.message);
        } else {
          setLoadError('Failed to load session');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, token, navigate]);

  const handleMockPay = async (): Promise<void> => {
    const result = await mockCompleteSession(sessionId, token);
    if (result.successUrl) {
      window.location.href = result.successUrl;
    } else {
      navigate('/success');
    }
  };

  const handleTossPay = async (): Promise<void> => {
    await requestTossCardPayment({
      sessionId,
      checkoutToken: token,
      orderId: sessionId,
      orderName: session!.productName,
      amount: Number(session!.grossAmount),
    });
    // requestPayment in the success path redirects the browser;
    // if we reach here the SDK returned without redirecting.
    throw new Error('Toss SDK did not redirect after requestPayment');
  };

  const handlePay = async (): Promise<void> => {
    if (!session) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (session.provider === 'toss') {
        await handleTossPay();
      } else {
        await handleMockPay();
      }
    } catch (err: unknown) {
      setSubmitError(mapSubmitError(err));
      setSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    if (session?.cancelUrl) {
      window.location.href = session.cancelUrl;
    } else {
      navigate('/cancel');
    }
  };

  if (loadError) {
    return (
      <main className="co-container">
        <div className="co-card">
          <h1>Something went wrong</h1>
          <p className="co-muted">{loadError}</p>
        </div>
      </main>
    );
  }

  if (!session) {
    return (
      <main className="co-container">
        <div className="co-card">
          <p className="co-muted">Loading…</p>
        </div>
      </main>
    );
  }

  return (
    <main className="co-container">
      <div className="co-card">
        <header className="co-header">
          <div className="co-brand">{session.clientTheme.clientName}</div>
          <div className="co-lock" aria-hidden="true">
            🔒 pay.crenverse
          </div>
        </header>

        <section className="co-section">
          <h2 className="co-section-title">Order Summary</h2>
          <div className="co-row">
            <span>{session.productName}</span>
            <span className="co-amount">
              {formatAmount(session.grossAmount, session.currency)}
            </span>
          </div>
          <div className="co-divider" />
          <div className="co-row co-total">
            <span>Total</span>
            <span className="co-amount">
              {formatAmount(session.grossAmount, session.currency)}
            </span>
          </div>
        </section>

        <section className="co-section">
          <h2 className="co-section-title">Payment</h2>
          {session.provider === 'toss' ? (
            <button
              className="co-button co-button-primary"
              onClick={() => {
                void handlePay();
              }}
              disabled={submitting}
            >
              {submitting ? 'Opening Toss…' : 'Pay with Toss'}
            </button>
          ) : (
            <button
              className="co-button co-button-primary"
              onClick={() => {
                void handlePay();
              }}
              disabled={submitting}
            >
              {submitting ? 'Processing…' : `Pay (mock) via ${session.provider}`}
            </button>
          )}
          {submitError ? (
            <p className="co-error">{submitError}</p>
          ) : null}
          {session.provider !== 'toss' ? (
            <p className="co-muted co-small">
              Phase 0 PoC — this does not charge a real card. It calls the
              server's <code>mock-complete</code> endpoint.
            </p>
          ) : null}
        </section>

        <footer className="co-footer">
          <button
            className="co-button co-button-link"
            onClick={handleCancel}
            disabled={submitting}
          >
            Cancel and return to {session.clientTheme.clientName}
          </button>
          <p className="co-muted co-small">
            Operated by Crengine Inc.
          </p>
        </footer>
      </div>
    </main>
  );
}

function mapSubmitError(err: unknown): string {
  if (err instanceof TossSdkError) {
    switch (err.code) {
      case 'toss_client_key_missing':
        return 'Toss is not configured for this environment. Please contact support.';
      case 'toss_sdk_load_failed':
        return 'Could not load the Toss payment widget. Please check your network and try again.';
      case 'toss_request_payment_failed':
        return 'Toss payment could not be started. Please try again.';
    }
  }
  if (err instanceof PublicApiError) {
    return err.payload.message;
  }
  return 'Payment failed. Please try again.';
}

function formatAmount(minorUnits: string, currency: string): string {
  const amount = BigInt(minorUnits);
  // KRW has no subunits; USD/EUR/JPY cases are handled as-is. We
  // defer to Intl.NumberFormat for display formatting and use 0
  // fraction digits for KRW / JPY.
  const noFraction = currency === 'KRW' || currency === 'JPY';
  const asNumber = Number(amount) / (noFraction ? 1 : 100);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: noFraction ? 0 : 2,
      maximumFractionDigits: noFraction ? 0 : 2,
    }).format(asNumber);
  } catch {
    return `${asNumber} ${currency}`;
  }
}
